import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import type { CategoryDTO, PhotoUploadTicket, ProductDTO, SupplierDTO } from '@bifeai/shared';
import type { ZodError } from 'zod';
import { api, uploadToTicket } from './api';
import type { PreparedPhoto } from './image';
import { useSession } from './session';

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<CategoryDTO[]>('/categories'),
    staleTime: 60_000,
  });
}

export function useProducts(
  filters: {
    search?: string;
    categoryId?: string;
    includeInactive?: boolean;
  } = {},
) {
  const qs = new URLSearchParams();
  if (filters.search) qs.set('search', filters.search);
  if (filters.categoryId) qs.set('categoryId', filters.categoryId);
  if (filters.includeInactive) qs.set('includeInactive', '1');
  const query = qs.toString();
  return useQuery({
    queryKey: ['products', filters],
    queryFn: () => api.get<ProductDTO[]>(`/products${query ? `?${query}` : ''}`),
    placeholderData: keepPreviousData,
  });
}

export function useSuppliers(enabled = true) {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<SupplierDTO[]>('/suppliers'),
    enabled,
  });
}

/** Todo lo que cambia catálogo, precios o stock vuelve a pedirse. */
export async function invalidateInventory(qc: QueryClient): Promise<void> {
  await Promise.all(
    ['products', 'categories', 'stock', 'suppliers', 'purchases', 'notifications', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })),
  );
}

/**
 * Quién edita qué:
 *  - el catálogo (productos, categorías, fotos, Excel) lo maneja la casa central;
 *  - los precios: la central el general y el de cada sucursal; una sucursal, solo el suyo.
 */
export function useCatalogAccess() {
  const { me, can, isHeadquartersUser, selectedBranchId, multiBranch } = useSession();
  const canPrices = can('prices.manage');
  const ownBranch = me.membership.branchId;
  const branchName = (id: string | null) => me.branches.find((b) => b.id === id)?.name ?? null;
  /** Sucursal cuyo precio se está viendo: la propia, la elegida en el selector, o ninguna (precio general). */
  const priceBranchId = isHeadquartersUser ? selectedBranchId : ownBranch;
  return {
    canCatalog: canPrices && isHeadquartersUser,
    canPrices,
    canGeneralPrice: canPrices && isHeadquartersUser,
    priceBranchId,
    priceBranchName: branchName(priceBranchId),
    multiBranch,
    branchName,
  };
}

/** Sube la foto ya achicada: pide la URL firmada, sube directo y confirma. */
export async function uploadProductPhoto(productId: string, photo: PreparedPhoto): Promise<ProductDTO> {
  const ticket = await api.post<PhotoUploadTicket>(`/products/${productId}/photo/upload-url`, {
    contentType: photo.contentType,
    size: photo.blob.size,
  });
  await uploadToTicket(ticket, photo.blob);
  return api.post<ProductDTO>(`/products/${productId}/photo`, {
    key: ticket.key,
  });
}

/** Errores de zod a { campo: mensaje } con la ruta completa ("items.0.qty"). */
export function zodFieldErrors(error: ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.');
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}
